/* Copyright (c) 2018 - 2022 VMware, Inc. All rights reserved. -- VMware Confidential */

import { Injectable } from "@angular/core";
import { ClosableDialog } from "@shared/components/dialog/closable-dialog";

// Service to handle custom handling of ESCAPE keyboard event in case of a shown dialog
// It also takes care to restore last focused item before the dialog was opened.
// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class DialogKeyService {

    private _lastFocusedElement: Element = null;
    private _dialog: ClosableDialog = null;

    constructor() {
        window.addEventListener("keyup", this.keyUpHandler.bind(this), true);
    }

    public registerDialog(c: ClosableDialog): void {
        this._dialog = c;
        this._lastFocusedElement = document.activeElement;

        // Process dropdown buttons as they are not present when the dialog closes, and
        // there is no way to focus them. Focus the dropdown menu trigger in such cases.
        this.setDropdownLastFocusedElement();
    }

    public unregisterDialog(): void {
        if (this._lastFocusedElement) {
            const elementToFocus: HTMLElement =
                document.getElementById(this._lastFocusedElement.id);
            if (elementToFocus == this._lastFocusedElement) {
                // setTimeout() is used as otherwise the focus is not applied
                setTimeout(() => elementToFocus.focus());
            }
        }
        this._dialog = null;
    }

    private keyUpHandler(event: KeyboardEvent): void {
        if ((this._dialog === null) || (event.key !== "Escape")) {
            return;
        }

        event.stopImmediatePropagation();

        this._dialog.handleEsc();
    }

    private setDropdownLastFocusedElement(): void {
        if (this.isDropdownItem(this._lastFocusedElement)) {
            if (this.isDropdownMenu(this._lastFocusedElement.parentElement)) {
                this.processDropdownMenu(this._lastFocusedElement.parentElement);
            } else {
                console.warn("Dialog opened from clr-dropdown-item but clr-dropdown-menu was not found!");
            }
        }
    }

    private isDropdownItem(element: Element): boolean {
        return element.attributes.getNamedItem("clrdropdownitem") != null;
    }

    private isDropdownMenu(element: Element): boolean {
        return element && element.nodeName == "CLR-DROPDOWN-MENU";
    }

    private isDropdown(element: Element): boolean {
        return element && element.nodeName == "CLR-DROPDOWN";
    }

    private processDropdownMenu(dropdownMenu: HTMLElement): void {
        const dropdown: HTMLElement = dropdownMenu.parentElement;

        if (this.isDropdown(dropdown)) {
            // Check for nested dropdown
            if (this.isDropdownMenu(dropdown.parentElement)) {
                this.processDropdownMenu(dropdown.parentElement);
            } else {
                // Top level dropdown reached. Find the dropdown trigger.
                const dropdownMenuTrigger: Element =
                    Array.from(dropdown.children).find(
                        childNode => childNode.attributes.getNamedItem("clrdropdowntrigger") != null);

                if (dropdownMenuTrigger) {
                    this._lastFocusedElement = dropdownMenuTrigger;
                } else {
                    console.warn("Dropdown trigger not found.");
                }
            }
        } else {
            console.warn("Dropdown not found.");
        }
    }
}
